import { Injectable, inject } from '@angular/core';
import { IFrameworkModel } from '../model/framework.model';
import { ENV_CONFIG, EnvironmentConfig, PageResponse, PageUtil } from '@libs/vc-core-lib';
import { concat, map, Observable, toArray } from 'rxjs';
import { IFrameworkCriteriaDTO } from '../dto/framework.criteria.dto';
import { Page } from 'vcp-common-ui-lib';
import { IFrameworkSearchDTO } from '../dto/framework.search.dto';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { IBulkUploadActionResult } from '../model/bulk-upload-action-result';
import { IReferenceTaskModel } from '../model/reference-task.model';
import { IFrameworkComponentModel } from '../model/framework-component.model';
import { IBulkFrameworkDeleteResponse } from '../model/bulk-framework-delete-response.model';

@Injectable({
    providedIn: 'root',
})
export class FrameworkService {
    private readonly _httpClient: HttpClient = inject(HttpClient);
    private readonly _environment: EnvironmentConfig = inject(ENV_CONFIG);
    private readonly _referenceTaskPath = 'reference-task';
    private readonly _frameworkPath = 'framework';

    public getById(frameworkId: number): Observable<IFrameworkModel> {
        return this._httpClient.get<IFrameworkModel>(
            `${this._environment.APP.VC_FRAMEWORK_BASE_URL}/${this._frameworkPath}/${frameworkId}`
        );
    }

    public save(model: IFrameworkModel): Observable<IFrameworkModel> {
        return this._httpClient.post<IFrameworkModel>(
            `${this._environment.APP.VC_FRAMEWORK_BASE_URL}/${this._frameworkPath}`,
            model
        );
    }

    public update(model: IFrameworkModel): Observable<IFrameworkModel> {
        return this._httpClient.put<IFrameworkModel>(
            `${this._environment.APP.VC_FRAMEWORK_BASE_URL}/${this._frameworkPath}/${model.id}`,
            model
        );
    }

    public delete(id: number): Observable<void> {
        return this._httpClient.delete<void>(
            `${this._environment.APP.VC_FRAMEWORK_BASE_URL}/${this._frameworkPath}/${id}`
        );
    }

    public bulkDelete(frameworkIds: number[]): Observable<IBulkFrameworkDeleteResponse> {
        const options = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
            }),
            body: frameworkIds,
        };
        return this._httpClient.delete<IBulkFrameworkDeleteResponse>(
            `${this._environment.APP.VC_FRAMEWORK_BASE_URL}/${this._frameworkPath}`,
            options
        );
    }

    public search(searchDTO: IFrameworkSearchDTO): Observable<Page<IFrameworkModel>> {
        const options = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
            }),
        };
        return this._httpClient
            .post<
                PageResponse<IFrameworkModel>
            >(`${this._environment.APP.VC_FRAMEWORK_BASE_URL}/${this._frameworkPath}/page-search`, searchDTO, options)
            .pipe(
                map((pageResponse: PageResponse<IFrameworkModel>) => {
                    return PageUtil.convertToPage(pageResponse);
                })
            );
    }

    public searchWithoutPaging(frameworkCriteriaDTO: IFrameworkCriteriaDTO): Observable<IFrameworkModel[]> {
        const options = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
            }),
        };
        return this._httpClient.post<IFrameworkModel[]>(
            `${this._environment.APP.VC_FRAMEWORK_BASE_URL}/${this._frameworkPath}/search`,
            frameworkCriteriaDTO,
            options
        );
    }

    public listFrameworksByIds(ids: number[]): Observable<IFrameworkModel[]> {
        const options = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
            }),
        };
        return this._httpClient.post<IFrameworkModel[]>(
            `${this._environment.APP.VC_FRAMEWORK_BASE_URL}/${this._frameworkPath}/list`,
            ids,
            options
        );
    }

    /** Framework Components **/

    public addFrameworkComponent(frameworkId: number, frameworkComponentId: number): Observable<IFrameworkModel> {
        return this._httpClient.post<IFrameworkModel>(
            `${this._environment.APP.VC_FRAMEWORK_BASE_URL}/${this._frameworkPath}/${frameworkId}/framework-component/${frameworkComponentId}`,
            {}
        );
    }

    public addFrameworkComponents(frameworkId: number, frameworkComponentIds: number[]): Observable<IFrameworkModel> {
        const calls = frameworkComponentIds.map((id) => this.addFrameworkComponent(frameworkId, id));
        // needs to called sequentially for the server to make one change at a time
        return concat(...calls).pipe(
            toArray<IFrameworkModel>(),
            map((frameworks) => frameworks[frameworks.length - 1])
        );
    }

    public removeFrameworkComponent(frameworkId: number, frameworkComponentId: number): Observable<IFrameworkModel> {
        return this._httpClient.delete<IFrameworkModel>(
            `${this._environment.APP.VC_FRAMEWORK_BASE_URL}/${this._frameworkPath}/${frameworkId}/framework-component/${frameworkComponentId}`
        );
    }

    public removeFrameworkComponents(frameworkId: number, componentNames: number[]): Observable<IFrameworkModel> {
        const calls = componentNames.map((componentName) => this.removeFrameworkComponent(frameworkId, componentName));
        // needs to called sequentially for the server to make one change at a time
        return concat(...calls).pipe(
            toArray<IFrameworkModel>(),
            map((frameworks) => frameworks[frameworks.length - 1])
        );
    }

    public downloadEmptyTemplate(): Observable<Blob> {
        const url = `${this._environment.APP.VC_FRAMEWORK_BASE_URL}/${this._referenceTaskPath}/bulk-template`;
        return this._httpClient.get(url, { responseType: 'blob' });
    }

    public downloadFilledTemplate(referenceTaskIds: number[]): Observable<Blob> {
        const url = `${this._environment.APP.VC_FRAMEWORK_BASE_URL}/${this._referenceTaskPath}/bulk-template`;
        return this._httpClient.post(url, referenceTaskIds, { responseType: 'blob' });
    }

    public uploadBulkTemplate(file: File): Observable<IBulkUploadActionResult> {
        const url = `${this._environment.APP.VC_FRAMEWORK_BASE_URL}/${this._referenceTaskPath}/upload/bulk-template`;
        const formData = new FormData();
        formData.append('file', file);
        return this._httpClient.post<IBulkUploadActionResult>(url, formData);
    }

    public orderTasksById(frameworkId: number, ids: number[]): Observable<IReferenceTaskModel[]> {
        return this._httpClient.put<IReferenceTaskModel[]>(
            `${this._environment.APP.VC_FRAMEWORK_BASE_URL}/${this._frameworkPath}/reference-task/order/${frameworkId}`,
            ids
        );
    }

    public orderComponentByFrameworkId(frameworkId: number, ids: number[]): Observable<IFrameworkComponentModel[]> {
        return this._httpClient.put<IFrameworkComponentModel[]>(
            `${this._environment.APP.VC_FRAMEWORK_BASE_URL}/${this._frameworkPath}/component/order/${frameworkId}`,
            ids
        );
    }
}
